/**
 * GHG report HTML transforms applied only for Word (html-docx-js) export.
 * UI markup stays unchanged; section head bars and subheads are injected here.
 */

import {
  applyGhgTocPageDesignForWord,
  applyGhgWordExportDesignUpgrades,
  getGhgCoverLogoWordInlineStyle,
  GHG_WORD_EXPORT_DESIGN_TOKENS,
} from './ghgWordExportDesign';
import {
  flattenGhgTocNestedTablesInDocument,
  migrateGhgTocLayoutInDocument,
} from './reportTocWordHtml';

const TOC_SHELL_PADDING_WORD = GHG_WORD_EXPORT_DESIGN_TOKENS.tocShellPaddingWord;
const TOC_PAGE_WIDTH = GHG_WORD_EXPORT_DESIGN_TOKENS.tocPageWidth;
const TOC_CONTENT_LAYOUT_MIN_HEIGHT = GHG_WORD_EXPORT_DESIGN_TOKENS.tocContentLayoutMinHeight;
const TOC_FOOTER_CONTENT_GAP = GHG_WORD_EXPORT_DESIGN_TOKENS.tocFooterContentGap;
const TOC_FOOTER_SPACER_HEIGHT = GHG_WORD_EXPORT_DESIGN_TOKENS.tocFooterSpacerHeight;
const TOC_CARD_BORDER = GHG_WORD_EXPORT_DESIGN_TOKENS.tocCardBorder;
const TOC_CARD_RADIUS = GHG_WORD_EXPORT_DESIGN_TOKENS.tocCardRadius;

const GHG_WORD_HEAD_BAR_CLASS = 'ghg-section-head-bar';
const GHG_WORD_SUBHEAD_CLASS = 'ghg-section-subhead-title';

const GHG_WORD_HEAD_BAR_SECTION_IDS = new Set([
  'Section_A',
  'Section_B',
  'Section_D',
  'Section_E', 
]);

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const findCardSection = (sectionEl) => {
  if (!sectionEl) return null;
  return (
    sectionEl.querySelector('.ETA_card_Section')
    || sectionEl.querySelector('[class*="ETA_card_Section"]')
  );
};

const ensureHeadBarClass = (headEl) => {
  if (!headEl) return;
  const classes = (headEl.getAttribute('class') || '')
    .split(/\s+/)
    .filter(Boolean);
  if (!classes.includes(GHG_WORD_HEAD_BAR_CLASS)) {
    classes.push(GHG_WORD_HEAD_BAR_CLASS);
  }
  headEl.setAttribute('class', classes.join(' '));
};

const BORDER_ONLY_STYLE_PROPS = new Set([
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-width',
  'border-style',
  'border-color',
  'border-radius',
  '-webkit-border-radius',
  '-moz-border-radius',
  'outline',
  'box-shadow',
]);

const BORDER_PADDING_STYLE_PROPS = new Set([
  ...BORDER_ONLY_STYLE_PROPS,
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
]);

/** Page modal shells only (cover/TOC card) — UI keeps border/padding; export strips these. */
const GHG_WORD_PAGE_MODAL_SHELL_CLASSES = [
  'ghg-cover-page',
  'ghg-word-export-page',
  'word-cover-page',
  'word-cover-content',
];

const WRAPPER_CLASS_MARKERS = [
  'Main_Section_card',
  'ETA_card_main',
  'ETA_card_center',
  'ETA_card_Section_sub',
  'ETA_card_Section_head',
  'ETA_card_Section',
  'ghg-word-cover',
  'ghg-section',
];

const filterStyleAttr = (styleStr, propsToRemove) =>
  String(styleStr || '')
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(':')[0]?.trim().toLowerCase();
      return prop && !propsToRemove.has(prop);
    })
    .join('; ');

const stripBorderPaddingFromStyleAttr = (styleStr) =>
  filterStyleAttr(styleStr, BORDER_PADDING_STYLE_PROPS);

const stripBorderOnlyFromStyleAttr = (styleStr) =>
  filterStyleAttr(styleStr, BORDER_ONLY_STYLE_PROPS);

const stripBorderPaddingFromElement = (el) => {
  if (!el?.getAttribute) return;
  const cleanedStyle = stripBorderPaddingFromStyleAttr(el.getAttribute('style'));
  if (cleanedStyle) {
    el.setAttribute('style', cleanedStyle);
  } else {
    el.removeAttribute('style');
  }
  el.removeAttribute('border');
};

const stripBorderOnlyFromElement = (el) => {
  if (!el?.getAttribute) return;
  const cleanedStyle = stripBorderOnlyFromStyleAttr(el.getAttribute('style'));
  if (cleanedStyle) {
    el.setAttribute('style', cleanedStyle);
  } else {
    el.removeAttribute('style');
  }
  el.removeAttribute('border');
};

const GHG_WORD_COVER_EXPORT_CLASS = 'ghg-word-cover-export';

const mergeInlineStyle = (el, addition) => {
  if (!el?.setAttribute) return;
  let cur = (el.getAttribute('style') || '').trim();
  if (cur && !cur.endsWith(';')) cur += ';';
  el.setAttribute('style', `${cur} ${addition}`.trim());
};

/** Export-only: drop cover shell top/bottom padding; keep horizontal inset. */
const flattenCoverShellPaddingShorthand = (styleStr) =>
  String(styleStr || '').replace(
    /padding:\s*([^;]+)/gi,
    (match, raw) => {
      const parts = raw.trim().split(/\s+/);
      if (parts.length === 4) {
        return `padding: 0 ${parts[1]} 0 ${parts[3]}`;
      }
      if (parts.length === 3) {
        return `padding: 0 ${parts[1]} 0 ${parts[2]}`;
      }
      if (parts.length === 2) {
        return `padding: 0 ${parts[1]}`;
      }
      return 'padding: 0';
    },
  );

const normalizeCoverTdVerticalSpacing = (td) => {
  if (!td?.getAttribute) return;
  let style = td.getAttribute('style') || '';

  if (/padding:\s*0\s+0\s+70pt\s+0/i.test(style)) {
    style = style.replace(/padding:\s*0\s+0\s+70pt\s+0/i, 'padding:0 0 12pt 0');
  }
  if (/padding:\s*0\s+0\s+26pt\s+0/i.test(style)) {
    style = style.replace(/padding:\s*0\s+0\s+26pt\s+0/i, 'padding:0 0 12pt 0');
  }
  if (/height:\s*230pt/i.test(style)) {
    style = style
      .replace(/height:\s*230pt/gi, 'height:64pt')
      .replace(/line-height:\s*230pt/gi, 'line-height:64pt');
  }

  if (style !== td.getAttribute('style')) {
    td.setAttribute('style', style);
  }
};

/** Word export only — trim cover top/bottom padding, margin, and large vertical spacers. */
const normalizeGhgCoverPageSpacingForWord = (doc) => {
  const shells = [];
  doc.querySelectorAll('[style], [class]').forEach((el) => {
    if (isGhgCoverPageShellElement(el)) {
      shells.push(el);
    }
  });

  shells.forEach((shell) => {
    if (shell.matches('td, th, table, tr')) return;

    const cls = (shell.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!cls.includes(GHG_WORD_COVER_EXPORT_CLASS)) {
      cls.push(GHG_WORD_COVER_EXPORT_CLASS);
      shell.setAttribute('class', cls.join(' '));
    }

    let style = flattenCoverShellPaddingShorthand(shell.getAttribute('style') || '');
    shell.setAttribute('style', style);
    mergeInlineStyle(
      shell,
      'padding-top:0 !important;padding-bottom:0 !important;margin-top:0 !important;margin-bottom:0 !important;margin-left:auto;margin-right:auto;',
    );

    shell.querySelectorAll('td[style]').forEach(normalizeCoverTdVerticalSpacing);

    shell.querySelectorAll('tr:first-child img').forEach((img) => {
      if (
        img.classList.contains('ghg-cover-meta-icon-img')
        || img.closest('.ghg-cover-label-badge, .ghg-cover-label-badge-img, .ghg-toc-label-badge-img')
      ) {
        return;
      }
      const cls = (img.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (!cls.includes('ghg-cover-logo')) {
        cls.push('ghg-cover-logo');
        img.setAttribute('class', cls.join(' '));
      }
      mergeInlineStyle(img, getGhgCoverLogoWordInlineStyle());
      img.removeAttribute('height');
      img.removeAttribute('width');
    });

    shell.querySelectorAll('.report-company-logo img').forEach((img) => {
      if (
        img.classList.contains('ghg-cover-meta-icon-img')
        || img.closest('.ghg-cover-label-badge, .ghg-cover-label-badge-img')
      ) {
        return;
      }
      const imgCls = (img.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (!imgCls.includes('ghg-cover-logo')) {
        imgCls.push('ghg-cover-logo');
        img.setAttribute('class', imgCls.join(' '));
      }
      mergeInlineStyle(img, getGhgCoverLogoWordInlineStyle());
      img.removeAttribute('height');
      img.removeAttribute('width');
    });

    shell.querySelectorAll('table[style*="min-height"]').forEach((table) => {
      let tableStyle = table.getAttribute('style') || '';
      tableStyle = tableStyle.replace(/min-height:\s*[^;]+;?/gi, '');
      table.setAttribute('style', tableStyle);
      mergeInlineStyle(table, 'min-height:auto;mso-height-rule:auto;');
    });
  });
};

const isGhgWordPageModalShell = (el) => {
  if (!el?.getAttribute || el.matches('table, thead, tbody, tr, th, td')) return false;
  const cls = el.getAttribute('class') || '';
  if (GHG_WORD_PAGE_MODAL_SHELL_CLASSES.some((marker) => cls.includes(marker))) {
    return true;
  }
  const style = el.getAttribute('style') || '';
  const parentClass = el.parentElement?.getAttribute('class') || '';
  if (parentClass.includes('ghg-word-toc') && isGhgWordPageShellStyle(style)) {
    return true;
  }
  return isGhgWordPageShellStyle(style);
};

const isGhgWordExportWrapper = (el) => {
  if (!el?.getAttribute) return false;
  const cls = el.getAttribute('class') || '';
  if (cls.includes(GHG_WORD_HEAD_BAR_CLASS)) return false;
  if (isGhgWordPageModalShell(el)) return true;
  if (el.matches('table, thead, tbody, tr, th, td')) return false;

  if (WRAPPER_CLASS_MARKERS.some((marker) => cls.includes(marker))) {
    return true;
  }
  if (el.matches('section.ghg-section')) {
    return true;
  }

  return false;
};

/** GHG front cover page shell (saved HTML) — export removes border only, keeps padding. */
const isGhgWordCoverPageShellStyle = (styleStr) => {
  const style = String(styleStr || '');
  if (!style || !/page-break-after:\s*always/i.test(style)) return false;
  return (
    /border:\s*1px\s+solid/i.test(style)
    || /8\.27in|11\.69in/i.test(style)
  );
};

const COVER_BORDER_NONE_INLINE =
  'border:none;border-width:0;border-style:none;outline:none;box-shadow:none;border-radius:0;mso-border-alt:none;';

const COVER_CELL_BORDER_NONE_INLINE =
  'border:none;border-top:none;border-right:none;border-bottom:none;border-left:none;border-width:0;border-style:none;mso-border-alt:none;mso-border-top-alt:none;mso-border-bottom-alt:none;mso-border-left-alt:none;mso-border-right-alt:none;';

const forceCoverElementNoBorder = (el) => {
  if (!el?.setAttribute) return;
  const cleaned = stripBorderOnlyFromStyleAttr(el.getAttribute('style') || '');
  el.setAttribute('style', cleaned ? `${cleaned};${COVER_CELL_BORDER_NONE_INLINE}` : COVER_CELL_BORDER_NONE_INLINE);
  el.removeAttribute('border');
};

/** True only for the GHG front-cover card shell (not TOC). */
const isGhgLiveOrSavedCoverShellElement = (el) => {
  if (!el?.getAttribute) return false;
  if (el.matches('td, th, table, tr, img')) return false;
  if (el.closest('.ghg-word-toc')) return false;

  const cls = el.getAttribute('class') || '';
  const id = el.getAttribute('id') || '';
  if (/(^|\s)ghg-cover-page(\s|$)/.test(cls)) return true;
  if (/(^|\s)ghg-word-export-page(\s|$)/.test(cls)) return true;
  if (/(^|\s)GHGSection_cover(\s|$)/.test(cls)) return true;
  if (id === 'Section_Cover') return true;

  const style = el.getAttribute('style') || '';
  if (isGhgWordCoverPageShellStyle(style)) return true;

  return false;
};

const isGhgCoverPageShellElement = isGhgLiveOrSavedCoverShellElement;

const isGhgWordPageShellStyle = (styleStr) => {
  const style = String(styleStr || '');
  if (!style) return false;
  if (isGhgWordCoverPageShellStyle(style)) return false;
  const hasCardBorder = /border:\s*1px\s+solid\s+#e5e7eb/i.test(style);
  const hasPageSize = /8\.27in|11\.69in/i.test(style);
  const hasShellPadding = /padding:\s*5[0-9]pt/i.test(style);
  if (hasCardBorder && (hasPageSize || hasShellPadding)) {
    return true;
  }
  if (hasPageSize && hasCardBorder) {
    return true;
  }
  return false;
};

const stripGhgTocLayoutTablesForWord = (tocRoot) => {
  if (!tocRoot?.querySelectorAll) return;
  tocRoot.querySelectorAll('table').forEach((table) => {
    const tblCls = (table.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!tblCls.includes('ghg-toc-layout-table')) {
      tblCls.push('ghg-toc-layout-table');
      table.setAttribute('class', tblCls.join(' '));
    }
    table.setAttribute('border', '0');
    table.setAttribute('cellpadding', '0');
    table.setAttribute('cellspacing', '0');
    forceCoverElementNoBorder(table);
    mergeInlineStyle(
      table,
      'border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;',
    );
    const isFooterBarTable = tblCls.includes('ghg-toc-footer-bar');
    table.querySelectorAll('td, th, tr').forEach((cell) => {
      const cellCls = (cell.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (
        cell.classList.contains('ghg-toc-badge-cell')
        || cell.classList.contains('ghg-toc-text-cell')
        || cell.classList.contains('ghg-toc-footer-gap-cell')
        || cell.classList.contains('ghg-toc-footer-spacer-cell')
        || cell.classList.contains('ghg-toc-footer-bar-cell')
        || cell.classList.contains('ghg-toc-footer-content-cell')
        || isFooterBarTable
      ) {
        cell.removeAttribute('border');
        return;
      }
      if (!cellCls.includes('ghg-toc-cell')) {
        cellCls.push('ghg-toc-cell');
        cell.setAttribute('class', cellCls.join(' '));
      }
      forceCoverElementNoBorder(cell);
    });
  });
};

const stripAllTocTablesInDocument = (doc) => {
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll('.ghg-word-toc').forEach((toc) => {
    stripGhgTocLayoutTablesForWord(toc);
  });
};

const normalizeGhgTocPageSpacingForWord = (doc) => {
  applyGhgTocPageDesignForWord(doc);
};

const regexNormalizeGhgTocPageSpacing = (html) => {
  const gutter = GHG_WORD_EXPORT_DESIGN_TOKENS.tocGridColGutter;
  const badgeGap = GHG_WORD_EXPORT_DESIGN_TOKENS.tocBadgeTextGap;
  return String(html || '')
    .replace(/padding:\s*54pt\s+56pt\s+44pt\s+56pt/gi, `padding:${TOC_SHELL_PADDING_WORD}`)
    .replace(/padding:\s*32pt\s+32pt\s+32pt\s+14pt/gi, `padding:${TOC_SHELL_PADDING_WORD}`)
    .replace(/padding:\s*36pt\s+40pt\s+36pt\s+28pt/gi, `padding:${TOC_SHELL_PADDING_WORD}`)
    .replace(
      /(<div[^>]*class=["'][^"']*ghg-toc-page-shell[^"']*["'][^>]*style=["'])([^"']*)(["'])/gi,
      (match, open, style, close) => {
        let next = style
          .replace(/margin:\s*0\s+auto/gi, 'margin:0 auto')
          .replace(/width:\s*100%/gi, `width:${TOC_PAGE_WIDTH}`)
          .replace(/max-width:\s*none/gi, `max-width:${TOC_PAGE_WIDTH}`);
        if (!new RegExp(`padding:\\s*${TOC_SHELL_PADDING_WORD.replace(/\s+/g, '\\s+')}`, 'i').test(next)) {
          next = next.replace(/padding:\s*[^;]+/gi, `padding:${TOC_SHELL_PADDING_WORD}`);
        }
        if (!/border:\s*1px\s+solid/i.test(next)) {
          next += `;border:1px solid ${TOC_CARD_BORDER}`;
        }
        if (!/border-radius:/i.test(next)) {
          next += `;border-radius:${TOC_CARD_RADIUS}`;
        }
        if (!new RegExp(`width:\\s*${TOC_PAGE_WIDTH.replace('.', '\\.')}`, 'i').test(next)) {
          next += `;width:${TOC_PAGE_WIDTH}`;
        }
        if (!new RegExp(`max-width:\\s*${TOC_PAGE_WIDTH.replace('.', '\\.')}`, 'i').test(next)) {
          next += `;max-width:${TOC_PAGE_WIDTH}`;
        }
        next = next.replace(/min-height:\s*[^;]+;?/gi, '');
        return `${open}${next}${close}`;
      },
    )
    .replace(
      /(<table[^>]*class=["'][^"']*ghg-toc-page-layout-table[^"']*["'][^>]*style=["'])([^"']*)(["'])/gi,
      (match, open, style, close) => {
        let next = style.replace(/min-height:\s*[^;]+;?/gi, '');
        if (!new RegExp(`min-height:\\s*${TOC_CONTENT_LAYOUT_MIN_HEIGHT.replace('.', '\\.')}`, 'i').test(next)) {
          next += `;min-height:${TOC_CONTENT_LAYOUT_MIN_HEIGHT}`;
        }
        return `${open}${next}${close}`;
      },
    )
    .replace(
      /margin:\s*28pt\s+0\s+0\s+0/gi,
      'margin:0',
    )
    .replace(
      /(<td[^>]*class=["'][^"']*ghg-toc-footer-gap-cell[^"']*["'][^>]*style=["'])([^"']*)(["'])/gi,
      (match, open, style, close) => {
        if (new RegExp(`height:\\s*${TOC_FOOTER_CONTENT_GAP.replace('.', '\\.')}`, 'i').test(style)) return match;
        return `${open}${style};height:${TOC_FOOTER_CONTENT_GAP};line-height:${TOC_FOOTER_CONTENT_GAP}${close}`;
      },
    )
    .replace(
      /(<td[^>]*class=["'][^"']*ghg-toc-footer-spacer-cell[^"']*["'][^>]*style=["'])([^"']*)(["'])/gi,
      (match, open, style, close) => {
        if (new RegExp(`height:\\s*${TOC_FOOTER_SPACER_HEIGHT.replace('.', '\\.')}`, 'i').test(style)) return match;
        return `${open}${style};height:${TOC_FOOTER_SPACER_HEIGHT}${close}`;
      },
    )
    .replace(/padding:\s*0\s+18pt\s+0\s+0/gi, `padding:0 ${gutter} 0 0`)
    .replace(/padding:\s*0\s+0\s+0\s+18pt/gi, `padding:0 0 0 ${gutter}`)
    .replace(/padding:\s*0\s+12pt\s+0\s+0/gi, `padding:0 ${badgeGap} 0 0`)
    .replace(/padding:\s*0\s+8pt\s+0\s+0/gi, `padding:0 ${badgeGap} 0 0`)
    .replace(/padding:\s*0\s+6pt\s+0\s+0/gi, `padding:0 ${badgeGap} 0 0`)
    .replace(
      /(<p[^>]*(?:ghg-toc-subtitle|font-size:9pt)[^>]*>)([\s\S]*?)(<\/p>)/gi,
      (match, open, text, close) => {
        if (text.includes('&#160;')) return match;
        return `${open}${text.replace(/ /g, '&#160;')}${close}`;
      },
    );
};

const stripGhgCoverLayoutTablesForWord = (coverRoot) => {
  if (!coverRoot?.querySelectorAll) return;
  coverRoot.querySelectorAll('table').forEach((table) => {
    const tblCls = (table.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!tblCls.includes('ghg-cover-layout-table')) {
      tblCls.push('ghg-cover-layout-table');
      table.setAttribute('class', tblCls.join(' '));
    }
    table.setAttribute('border', '0');
    table.setAttribute('cellpadding', '0');
    table.setAttribute('cellspacing', '0');
    forceCoverElementNoBorder(table);
    mergeInlineStyle(
      table,
      'border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;',
    );
    table.querySelectorAll('td, th, tr').forEach((cell) => {
      const cellCls = (cell.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (!cellCls.includes('ghg-cover-cell')) {
        cellCls.push('ghg-cover-cell');
        cell.setAttribute('class', cellCls.join(' '));
      }
      forceCoverElementNoBorder(cell);
    });
  });
};

/** DOM pass: strip every table/cell border inside cover shells (inline — Word ignores stylesheet overrides). */
const stripAllCoverTablesInDocument = (doc) => {
  if (!doc?.querySelectorAll) return;
  const shells = doc.querySelectorAll(
    '.ghg-cover-page, .ghg-word-cover-export, .ghg-word-export-page, .GHGSection_cover, #Section_Cover',
  );
  shells.forEach((shell) => {
    if (shell.matches('table, td, th, tr')) return;
    if (!isGhgCoverPageShellElement(shell) && !shell.matches('.GHGSection_cover, #Section_Cover')) return;
    forceCoverElementNoBorder(shell);
    stripGhgCoverLayoutTablesForWord(shell);
  });
  doc.querySelectorAll('.ghg-cover-page table, .ghg-word-export-page table').forEach((table) => {
    stripGhgCoverLayoutTablesForWord(table.closest('.ghg-cover-page, .ghg-word-export-page, .ghg-word-cover-export') || table.parentElement);
  });
};

/** Word export only: remove cover card border; padding and layout stay as in saved HTML. */
const stripGhgCoverPageBorderOnlyInDoc = (doc) => {
  const coverShells = [];
  doc.querySelectorAll('[style], [class]').forEach((el) => {
    if (!isGhgCoverPageShellElement(el)) return;
    if (el.matches('td, th, table, tr')) return;
    coverShells.push(el);

    stripBorderOnlyFromElement(el);
    const cls = (el.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!cls.includes(GHG_WORD_COVER_EXPORT_CLASS)) {
      cls.push(GHG_WORD_COVER_EXPORT_CLASS);
      el.setAttribute('class', cls.join(' '));
    }
    mergeInlineStyle(
      el,
      'border:none;border-width:0;outline:none;box-shadow:none;border-radius:0;mso-border-alt:none;',
    );
    stripGhgCoverLayoutTablesForWord(el);
  });

  doc.querySelectorAll('.ghg-cover-page, .ghg-word-export-page, .GHGSection_cover, #Section_Cover').forEach((shell) => {
    if (coverShells.includes(shell)) return;
    const cls = (shell.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!cls.includes(GHG_WORD_COVER_EXPORT_CLASS)) {
      cls.push(GHG_WORD_COVER_EXPORT_CLASS);
      shell.setAttribute('class', cls.join(' '));
    }
    stripGhgCoverLayoutTablesForWord(shell);
    mergeInlineStyle(shell, 'border:none;border-width:0;mso-border-alt:none;');
  });
};

const stripGhgWordExportWrapperBorderAndPadding = (doc) => {
  flattenGhgTocNestedTablesInDocument(doc);
  migrateGhgTocLayoutInDocument(doc);
  stripAllCoverTablesInDocument(doc);
  stripAllTocTablesInDocument(doc);
  normalizeGhgTocPageSpacingForWord(doc);
  stripGhgCoverPageBorderOnlyInDoc(doc);

  doc.querySelectorAll('[style]').forEach((el) => {
    if (isGhgCoverPageShellElement(el)) return;
    if (el.closest('.ghg-word-toc')) return;

    if (isGhgWordExportWrapper(el)) {
      if (el.matches('td, th')) return;
      if (el.closest('.ghg-toc-badge-table, .ghg-toc-row-table')) return;
      stripBorderPaddingFromElement(el);
    }
  });

  normalizeGhgCoverPageSpacingForWord(doc);
};

const patchCoverOpenTagBorder = (openTag) => {
  let tag = openTag;
  const hasCoverClass =
    /\bghg-cover-page\b/i.test(tag)
    || /\bGHGSection_cover\b/i.test(tag)
    || /\bid=["']Section_Cover["']/i.test(tag)
    || /\bghg-word-export-page\b/i.test(tag);

  const styleMatch = tag.match(/\bstyle=(["'])([\s\S]*?)\1/i);
  const styleVal = styleMatch?.[2] || '';
  const isCoverByStyle = isGhgWordCoverPageShellStyle(styleVal);

  if (!hasCoverClass && !isCoverByStyle) {
    return tag;
  }

  if (styleMatch) {
    const quote = styleMatch[1];
    const cleaned = stripBorderOnlyFromStyleAttr(styleVal);
    const merged = `${cleaned ? `${cleaned};` : ''}${COVER_BORDER_NONE_INLINE}`;
    tag = tag.replace(styleMatch[0], `style=${quote}${merged}${quote}`);
  } else {
    tag = tag.replace(/>$/, ` style="${COVER_BORDER_NONE_INLINE}">`);
  }

  if (!/\bghg-word-cover-export\b/i.test(tag)) {
    if (/\bclass=(["'])([^"']*)\1/i.test(tag)) {
      tag = tag.replace(/\bclass=(["'])([^"']*)\1/i, (m, q, c) =>
        `class=${q}${c} ghg-word-cover-export${q}`,
      );
    } else {
      tag = tag.replace(/>$/, ' class="ghg-word-cover-export">');
    }
  }

  return tag;
};

/**
 * Final string pass — html-docx-js relies on inline styles; strips cover border regardless of attr order.
 * @param {string} html
 */
export function forceStripGhgCoverPageBorderFromHtml(html) {
  let out = String(html || '');

  if (typeof DOMParser !== 'undefined') {
    const isFullDoc = /<html[\s>]/i.test(out);
    const doc = new DOMParser().parseFromString(
      isFullDoc ? out : `<div id="ghg-cover-strip-root">${out}</div>`,
      'text/html',
    );
    stripAllCoverTablesInDocument(doc);
    stripGhgCoverPageBorderOnlyInDoc(doc);
    if (isFullDoc) {
      out = doc.documentElement?.outerHTML || out;
    } else {
      const root = doc.getElementById('ghg-cover-strip-root');
      out = root ? root.innerHTML : out;
    }
  }

  out = out.replace(/<(div|section)\b([^>]*)>/gi, (full, tagName, attrs) =>
    patchCoverOpenTagBorder(`<${tagName}${attrs}>`),
  );

  out = out.replace(
    /\sstyle=(["'])([\s\S]*?)\1/gi,
    (match, quote, style) => {
      if (!isGhgWordCoverPageShellStyle(style)) {
        return match;
      }
      const cleaned = stripBorderOnlyFromStyleAttr(style);
      const merged = cleaned ? `${cleaned};${COVER_BORDER_NONE_INLINE}` : COVER_BORDER_NONE_INLINE;
      return ` style=${quote}${merged}${quote}`;
    },
  );

  return out;
}

/** Regex fallback: cover page shell only — border removed, padding unchanged. */
const regexStripCoverPageBorder = (html) => forceStripGhgCoverPageBorderFromHtml(html);

/**
 * Cover page fragment — border removed only (Word export). UI/saved HTML unchanged on disk.
 * @param {string} html
 */
export function stripGhgCoverPageBorderForWord(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') {
    return regexStripCoverPageBorder(html);
  }

  const normalized = html.replace(/\bclassName=/g, 'class=');
  const doc = new DOMParser().parseFromString(
    `<div id="ghg-cover-border-root">${normalized}</div>`,
    'text/html',
  );
  const root = doc.getElementById('ghg-cover-border-root');
  if (!root) return regexStripCoverPageBorder(normalized);

  root.querySelectorAll('[style], [class]').forEach((el) => {
    if (!isGhgCoverPageShellElement(el)) return;
    if (el.matches('td, th, table, tr')) return;
    stripBorderOnlyFromElement(el);
    const cls = (el.getAttribute('class') || '').split(/\s+/).filter(Boolean);
    if (!cls.includes(GHG_WORD_COVER_EXPORT_CLASS)) {
      cls.push(GHG_WORD_COVER_EXPORT_CLASS);
      el.setAttribute('class', cls.join(' '));
    }
    mergeInlineStyle(
      el,
      'border:none;border-width:0;outline:none;box-shadow:none;border-radius:0;mso-border-alt:none;',
    );
    stripGhgCoverLayoutTablesForWord(el);
  });

  return forceStripGhgCoverPageBorderFromHtml(root.innerHTML);
}

/** Regex fallback: cover page — trim top/bottom padding, margin, and vertical spacers. */
const regexNormalizeCoverPageVerticalSpacing = (html) =>
  String(html || '')
    .replace(
      /padding:\s*54pt\s+60pt\s+46pt\s+60pt/gi,
      'padding:0 60pt 0 60pt',
    )
    .replace(/padding:\s*0\s+0\s+70pt\s+0/gi, 'padding:0 0 12pt 0')
    .replace(/padding:\s*0\s+0\s+26pt\s+0/gi, 'padding:0 0 12pt 0')
    .replace(/height:\s*230pt/gi, 'height:64pt')
    .replace(/line-height:\s*230pt/gi, 'line-height:64pt')
    .replace(/height:\s*40pt/gi, `height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-height:\s*40pt/gi, `max-height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/height:\s*50pt/gi, `height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-height:\s*50pt/gi, `max-height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/height:\s*28pt/gi, `height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-height:\s*28pt/gi, `max-height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-width:\s*52pt/gi, `max-width:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoMaxWidthPt}`)
    .replace(/max-height:\s*50px/gi, `max-height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-height:\s*42pt/gi, `max-height:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt}`)
    .replace(/max-width:\s*88pt/gi, `max-width:${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoMaxWidthPt}`)
    .replace(
      /\sstyle=(["'])([\s\S]*?)\1/gi,
      (match, quote, style) => {
        if (!isGhgWordCoverPageShellStyle(style)) {
          return match;
        }
        let next = flattenCoverShellPaddingShorthand(style);
        next = next
          .replace(/\bmargin-top:\s*[^;]+;?/gi, '')
          .replace(/\bmargin-bottom:\s*[^;]+;?/gi, '')
          .replace(/\bpadding-top:\s*[^;]+;?/gi, '')
          .replace(/\bpadding-bottom:\s*[^;]+;?/gi, '');
        const merged = `${next}; padding-top:0; padding-bottom:0; margin-top:0; margin-bottom:0; margin-left:auto; margin-right:auto;`.replace(
          /;\s*;/g,
          ';',
        );
        return ` style=${quote}${merged}${quote}`;
      },
    );

/** ReportExportModal wraps cover in .word-cover-page (padding:1in); flatten for DOCX only. */
const flattenWordCoverPageWrapper = (html) =>
  String(html || '')
    .replace(
      /<div\s+class="word-cover-page"[^>]*>\s*<div\s+class="word-cover-content"[^>]*>/gi,
      '<div class="ghg-word-export-page">',
    )
    .replace(
      /<div\s+class='word-cover-page'[^>]*>\s*<div\s+class='word-cover-content'[^>]*>/gi,
      "<div class='ghg-word-export-page'>",
    );

/**
 * @param {string} html - GHG report body HTML (outerHTML or fragment)
 * @returns {string} HTML with Word-only section head bars / subheads
 */
export function prepareGhgHtmlForWordExport(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;

  const normalized = html.replace(/\bclassName=/g, 'class=');
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="ghg-word-export-root">${normalized}</div>`,
    'text/html',
  );
  const root = doc.getElementById('ghg-word-export-root');
  if (!root) return html;

  GHG_WORD_HEAD_BAR_SECTION_IDS.forEach((sectionId) => {
    const section = doc.getElementById(sectionId);
    if (!section) return;

    const cardSection = findCardSection(section);
    if (!cardSection) return;

    const headTitle = section.getAttribute('data-word-section-head');
    let headEl = cardSection.querySelector('.ETA_card_Section_head');

    if (!headEl && headTitle) {
      headEl = doc.createElement('div');
      headEl.setAttribute('class', 'ETA_card_Section_head text-left ghg-section-head-bar');
      headEl.innerHTML = `<h5>${escapeHtml(headTitle)}</h5>`;
      const subEl = cardSection.querySelector('.ETA_card_Section_sub');
      if (subEl) {
        cardSection.insertBefore(headEl, subEl);
      } else {
        cardSection.insertBefore(headEl, cardSection.firstChild);
      }
    } else if (headEl) {
      ensureHeadBarClass(headEl);
    }

    if (sectionId === 'Section_B') {
      const subheadText = section.getAttribute('data-word-section-subhead');
      const subEl = cardSection.querySelector('.ETA_card_Section_sub');
      if (subheadText && subEl && !subEl.querySelector(`.${GHG_WORD_SUBHEAD_CLASS}`)) {
        const subheadEl = doc.createElement('h6');
        subheadEl.setAttribute('class', `${GHG_WORD_SUBHEAD_CLASS} mb-3`);
        subheadEl.textContent = subheadText;
        subEl.insertBefore(subheadEl, subEl.firstChild);
      }
    }
  });

  stripGhgWordExportWrapperBorderAndPadding(doc);

  let result = root.innerHTML;
  result = regexNormalizeCoverPageVerticalSpacing(result);
  result = regexNormalizeGhgTocPageSpacing(result);
  result = flattenWordCoverPageWrapper(result);
  result = applyGhgWordExportDesignUpgrades(result);
  result = regexNormalizeGhgTocPageSpacing(result);
  return forceStripGhgCoverPageBorderFromHtml(result);
}

/**
 * Full GHG export document (multi-page HTML string). Use before html-docx-js when the
 * payload may include ReportExportModal cover wrappers or saved cover/TOC page markup.
 */
export function prepareGhgWordExportDocument(html) {
  if (!html || typeof html !== 'string') return html;
  const isFullDocument = /<html[\s>]/i.test(html);
  let result = html.replace(/\bclassName=/g, 'class=');
  result = flattenWordCoverPageWrapper(result);
  result = forceStripGhgCoverPageBorderFromHtml(result);
  result = regexNormalizeCoverPageVerticalSpacing(result);
  if (typeof DOMParser === 'undefined') {
    result = applyGhgWordExportDesignUpgrades(result);
    return forceStripGhgCoverPageBorderFromHtml(result);
  }
  const parser = new DOMParser();
  const doc = isFullDocument
    ? parser.parseFromString(result, 'text/html')
    : parser.parseFromString(
      `<div id="ghg-word-export-root">${result}</div>`,
      'text/html',
    );
  stripGhgWordExportWrapperBorderAndPadding(doc);
  if (isFullDocument) {
    result = doc.documentElement?.outerHTML || result;
  } else {
    const root = doc.getElementById('ghg-word-export-root');
    result = root ? root.innerHTML : result;
  }
  result = regexNormalizeCoverPageVerticalSpacing(result);
  result = regexNormalizeGhgTocPageSpacing(result);
  result = applyGhgWordExportDesignUpgrades(result);
  result = regexNormalizeGhgTocPageSpacing(result);
  return forceStripGhgCoverPageBorderFromHtml(result);
}

/**
 * TOC page fragment — migrate legacy layout, strip table borders, apply Word design.
 * @param {string} html
 */
export function prepareGhgTocPageHtmlForWord(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') {
    return applyGhgWordExportDesignUpgrades(html);
  }
  const doc = new DOMParser().parseFromString(
    `<div id="ghg-toc-export-root">${html.replace(/\bclassName=/g, 'class=')}</div>`,
    'text/html',
  );
  flattenGhgTocNestedTablesInDocument(doc);
  migrateGhgTocLayoutInDocument(doc);
  stripAllTocTablesInDocument(doc);
  normalizeGhgTocPageSpacingForWord(doc);
  const root = doc.getElementById('ghg-toc-export-root');
  let result = root ? root.innerHTML : html;
  result = applyGhgWordExportDesignUpgrades(result);
  return regexNormalizeGhgTocPageSpacing(result);
}

export function isGhgWordExportFileName(fileName) {
  return (fileName || '').startsWith('GHG_');
}
